import { configureStore } from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage';
import { FLUSH, PAUSE, PERSIST, persistReducer, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import { Reducer } from 'redux';

import { PLATFORM_FEATURE_KEY, platformReducers } from '@clatter/platform';
import { usersApi } from '../services';
import { baseApi } from '../services/baseApi';

import { AUTH_FEATURE_KEY, authSlice } from './auth.slice';
import { NG_IMPERSONATE_FEATURE_KEY, impersonateSlice } from './impersonate.slice';
import { THEME_UI_FEATURE_KEY, themeUIReducer } from './UI/theme.slice';
import { LONG_PULL_UI_FEATURE_KEY, longPullUIReducer } from './UI/longPull.slice';
import { PROJECT_STEPPER_FEATURE_KEY, projectStepperReducer } from './UI/projectStepper.slice';
import { ASSET_MANAGER_UI_FEATURE_KEY, assetManagerUIReducer } from './UI/assetManagerUI.slice';
import { NOTIFICATIONS_FEATURE_KEY, notificationsReducer } from './UI/notifications.slice';
import { NG_APP_FEATURE_KEY, ngAppSlice } from './UI/ngApp.slice';
import { VIEW_STATE_FEATURE_KEY, viewStateReducer } from './UI/viewState.slice';
import { PINNED_ITEMS_FEATURE_KEY, pinnedItemsSlice } from './UI/pinnedItems.slice';
import { RECENTLY_VISITED_FEATURE_KEY, recentlyVisitedSlice } from './UI/recentlyVisitedItems.slice';

export { setSelectedTheme } from './UI/theme.slice';
export {
  setSearchQuery,
  addDismissedSection,
  toggleSectionExpanded,
  removeDismissedSection,
  toggleHomeShowMore,
  ngAppSelectors
} from './UI/ngApp.slice';
export { stopImpersonating, setImpersonatedUser, impersonateSelectors } from './impersonate.slice';
export { setUserAuthState, logoutUser, checkSession, authSelectors } from './auth.slice';
export {
  addNotification,
  addLongPullUIItem,
  updateLongPullUIItem,
  removeLongPullUIItem,
} from './UI/notifications.slice';
export {
  setProjectStepStatus,
  setProjectStepData,
  clearProjectSteps,
  setProjectStepper,
  projectStepperSelectors,
} from './UI/projectStepper.slice';
export {
  // addLongPullUIItem,
  // updateLongPullUIItem,
  // removeLongPullUIItem,
  longPullUISelectors,
} from './UI/longPull.slice';
export {
  setNewButtonState,
  setSidebarStateItems,
  setSidebarStateSelectedItem,
  assetManagerUISelectors,
} from './UI/assetManagerUI.slice';
export {
  setViewState,
  setSelectedThemeId,
  setQuery,
  setCategoriesHierarchy,
  clearViewState,
  clearAllViewState,
  viewStateSelectors,
} from './UI/viewState.slice';
export { pinItem, unpinItem, pinnedItemsSelectors, EMPTY_PINNED_ITEMS } from './UI/pinnedItems.slice';
export { recordVisit, removeVisit, recentlyVisitedSelectors } from './UI/recentlyVisitedItems.slice';

export const store = configureStore({
  reducer: {
    [PLATFORM_FEATURE_KEY]: platformReducers,
    [NG_IMPERSONATE_FEATURE_KEY]: persistReducer(
      { key: NG_IMPERSONATE_FEATURE_KEY, storage: storage },
      impersonateSlice.reducer,
    ) as Reducer,
    [NG_APP_FEATURE_KEY]: persistReducer(
      { key: NG_APP_FEATURE_KEY, storage: storage },
      ngAppSlice.reducer,
    ) as Reducer,
    [AUTH_FEATURE_KEY]: authSlice.reducer,
    [baseApi.reducerPath]: baseApi.reducer,
    [THEME_UI_FEATURE_KEY]: themeUIReducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [LONG_PULL_UI_FEATURE_KEY]: longPullUIReducer,
    [NOTIFICATIONS_FEATURE_KEY]: notificationsReducer,
    [PROJECT_STEPPER_FEATURE_KEY]: projectStepperReducer,
    [ASSET_MANAGER_UI_FEATURE_KEY]: assetManagerUIReducer,
    [VIEW_STATE_FEATURE_KEY]: viewStateReducer,
    [PINNED_ITEMS_FEATURE_KEY]: persistReducer(
      { key: PINNED_ITEMS_FEATURE_KEY, storage: storage },
      pinnedItemsSlice.reducer,
    ) as Reducer,
    [RECENTLY_VISITED_FEATURE_KEY]: persistReducer(
      { key: RECENTLY_VISITED_FEATURE_KEY, storage: storage },
      recentlyVisitedSlice.reducer,
    ) as Reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          // ignore RTK Query actions that contain Blobs
          'baseApi/executeMutation/fulfilled',
        ],
        // ignore Blob data in RTK Query mutation state
        ignoredPaths: [/^baseApi\.mutations\./],
        ignoredActionPaths: ['payload', 'meta.arg', 'meta.baseQueryMeta'],
      },
    })
      .concat(baseApi.middleware)
      .concat(usersApi.middleware),
  devTools: process.env?.['NODE_ENV'] !== 'production',
});

export type RootState = ReturnType<typeof store.getState>;

export type AppDispatch = typeof store.dispatch;

export { useAppSelector, useAppDispatch } from './hooks';
